import React, { useState, useEffect, ReactElement } from "react";
import { useDispatch } from 'react-redux'
import type { Dispatch } from 'redux'
import { actions as newProjectWizardActions } from '../../../actions/newProjectWizardActions';
import * as types from '../../../constants/actionTypes';
import { NewProjectData, BaselineOption } from '../../../types/common';
import Notification from '../../Common/Notification';
import TutorialModal from "./TutorialModal";
import './LinkCopyTasks.scss';

interface LinkTasksProps {
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setProjectData: React.Dispatch<React.SetStateAction<NewProjectData>>;
    chooseDiffAddTaskMethod: () => void;
}

const LinkTasks = ({ setIsLoading, setProjectData, chooseDiffAddTaskMethod }: LinkTasksProps): ReactElement => {

    const dispatch: Dispatch = useDispatch();
    const listLinkBaselines = async () => dispatch(newProjectWizardActions.listLinkBaselines());

    const [availableBaselines, setAvailableBaselines] = useState<Array<BaselineOption>>([]);    // Keeping available & selected baselines here, not in parent
    const [selectedBaselines, setSelectedBaselines] = useState<Array<BaselineOption>>([]);
    const [showTutorial, setShowTutorial] = useState<boolean>(false);
    const [availableFilter, setAvailableFilter] = useState<string>('');
    const [selectedFilter, setSelectedFilter] = useState<string>('');

    useEffect(() => {
        loadFilters();
    }, []);

    const loadFilters = async () => {

        setIsLoading(true);

        try {
            const response = await listLinkBaselines();
            if (response.type === types.NEW_PROJECT_WIZARD_LIST_LINK_BASELINES_SUCCESS) {
                setAvailableBaselines(response.payload.baselines);
            } else if (response.type === types.NEW_PROJECT_WIZARD_LIST_LINK_BASELINES_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsLoading(false);
        }
    }

    const renderAvailableBaselines = () => {

        const filterLower = availableFilter.toLocaleLowerCase();

        return availableBaselines
            .filter((x) => !selectedBaselines.some((sl) => sl.baselineId === x.baselineId))  // Suppress any baselines that have been selected
            .filter(x => x.baselineName.toLocaleLowerCase().indexOf(filterLower) >= 0)
            .sort((a, b) => (a.baselineName > b.baselineName) ? 1 : -1)
            .map((baseline, index) => (
                <div className="list-group-item" key={'avail-baseline-' + index} id={baseline.baselineId.toString()} onClick={() => selectBaseline(baseline.baselineId)}>
                    <div className="d-flex w-100 justify-content-between">
                        <div className="flex-grow-1">
                            <p className="list-item-baseline">{baseline.baselineName}</p>
                            <p className="list-item-projects">{baseline.projectNames?.join(" ,")}</p>
                        </div>

                        <div className="list-item-icon ms-2 my-auto"><i className="fa-regular fa-plus mr-auto ml-auto"></i></div>
                    </div>
                </div>
            ));
    };

    const renderSelectedBaselines = () => {
        const filterLower = selectedFilter.toLocaleLowerCase();

        return selectedBaselines
            .filter(x => x.baselineName.toLocaleLowerCase().indexOf(filterLower) >= 0)
            .sort((a, b) => (a.baselineName > b.baselineName) ? 1 : -1)
            .map((baseline, index) => (
                <div className="list-group-item" key={'selected-baseline-' + index} id={baseline.baselineId.toString()} onClick={() => deselectBaseline(baseline.baselineId)}>
                    <div className="d-flex w-100 justify-content-between">
                        <div className="flex-grow-1">
                            <p className="list-item-baseline">{baseline.baselineName}</p>
                            <p className="list-item-projects">{baseline.projectNames?.join(" ,")}</p>
                        </div>
                        <div className="list-item-icon ms-2 my-auto"><i className="fa-regular fa-minus mr-auto ml-auto"></i></div>
                    </div>
                </div>
            ));
    }

    const selectBaseline = (baselineId: number) => {
        const selected = availableBaselines.find(x => x.baselineId === baselineId);

        setAvailableBaselines(existing => existing.filter(x => x.baselineId !== baselineId));
        setSelectedBaselines(existing => [...existing, selected]);

        setProjectData((existing) => ({
            ...existing,
            baselineIds: [...existing.baselineIds, selected.baselineId]
        }));
    };

    const deselectBaseline = (baselineId: number) => {
        const selected = selectedBaselines.find(x => x.baselineId === baselineId);

        setSelectedBaselines(existing => existing.filter(x => x.baselineId !== baselineId));
        setProjectData((existing) => ({
            ...existing,
            baselineIds: existing.baselineIds.filter(x => x !== baselineId)
        }));

        setAvailableBaselines(existing => [...existing, selected]);
    };

    const selectAll = () => {
        setSelectedBaselines(existing => [...existing, ...availableBaselines]);
        setAvailableBaselines([]);
    }

    const deselectAll = () => {
        setAvailableBaselines(existing => [...existing, ...selectedBaselines]);
        setSelectedBaselines([]);
    }

    return (
        <div className="link-tasks-content">
            <div className="wizard-content-title clearfix">
                <span className="float-start">Add tasks to your project by selecting from pre existing Baselines.</span>
                <button type="button" className="btn btn-link float-end" onClick={() => setShowTutorial(true)}>
                    <i className="fa-regular fa-info-circle fa-lg"></i>
                    &nbsp;&nbsp;Learn how Pacer organizes tasks.
                </button>
            </div>

            <div className="select-diff-method">
                <button type="button" className="btn btn-link" onClick={() => chooseDiffAddTaskMethod()}>
                    <i className="fa-regular fa-chevron-left fa-lg"></i>
                    &nbsp;&nbsp;Select Different Add Task Method
                </button>
            </div>

            <div className="container">

                <div className="row">

                    {/* Begin available */}
                    <div className="col-6">
                        <div className="row baselines-section-top">
                            <div className="col-9 my-3">
                                <span className="wizard-content-title">Available</span>
                            </div>
                            <div className="col-3">
                                {/* Future spot of "create new task list" */}
                            </div>
                        </div>

                        <div className="row baselines-section">
                            <div className="col-12 py-2">
                                <div className="input-group">
                                    <input type="text"
                                        className="form-control border-right-0 border"
                                        id="search-available"
                                        placeholder="Search by name"
                                        aria-label="Search by name"
                                        aria-describedby="search-addon"
                                        value={availableFilter}
                                        onChange={(e) => setAvailableFilter(e.target.value)} />
                                    <div className="input-group-append">
                                        <span className="input-group-text bg-transparent" id="search-addon"><i className="fa-regular fa-magnifying-glass"></i></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row baselines-section">
                            <div className="col-12 list-container overflow-auto">
                                <div className="list-group">
                                    {renderAvailableBaselines()}
                                </div>
                            </div>
                        </div>

                        <div className="row baselines-section-bottom">
                            <div className="col-12">
                                <button type="button" className="pacer61-btn pacer61-purple-btn" onClick={() => selectAll()}>Select All</button>
                            </div>
                        </div>
                    </div>

                    {/* Begin selected */}
                    <div className="col-6">
                        <div className="row baselines-section-top">
                            <div className="col-9 my-3">
                                <span className="wizard-content-title">Selected</span>
                            </div>
                        </div>

                        <div className="row baselines-section">
                            <div className="col-12 py-2">
                                <div className="input-group">
                                    <input type="text"
                                        className="form-control border-right-0 border"
                                        id="search-selected"
                                        placeholder="Search by name"
                                        aria-label="Search by name"
                                        aria-describedby="search-addon"
                                        value={selectedFilter}
                                        onChange={(e) => setSelectedFilter(e.target.value)} />
                                    <div className="input-group-append">
                                        <span className="input-group-text bg-transparent" id="search-addon"><i className="fa-regular fa-magnifying-glass"></i></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row baselines-section">
                            <div className="col-12 list-container overflow-auto">
                                <ul className="list-group">
                                    {renderSelectedBaselines()}
                                </ul>
                            </div>
                        </div>

                        <div className="row baselines-section-bottom">
                            <div className="col-12">
                                <button type="button" className="pacer61-purple-btn" onClick={() => deselectAll()}>Remove All</button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {
                showTutorial &&
                <TutorialModal setShowTutorial={setShowTutorial} />
            }

        </div>
    );
}

export default LinkTasks;
